import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { decrypt } from "../../helpers/utilities";
import { URLS } from "../../helpers/URLS";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField, Button } from "@mui/material";
import useAxios from "../../helpers/Hooks/useAxios";
import Accordion from 'react-bootstrap/Accordion';
import NoData from "../../loaders/NoData";
import Description from "../Common/Description";
import SkeletonLoader from "../../loaders/Skeleton";
import moment from "moment";
import PaginationGlobal from "../TablePagination.js/PaginationGlobal";
import { toast } from 'react-toastify';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import '../../assets/scss/tables.scss';

const ViewStatus = () => {

    const { fetchData } = useAxios();

    const { parameter, value } = useParams();

    /* The table that opened this page knows the name and the team size, and
       passes them through. Opened directly, both fall back to what can be
       read off the rows themselves. */
    const { state } = useLocation();

    const [isProject, setIsProject] = useState(false);

    const [isNodata, setNodata] = useState(false);

    const [pageNumber, setPageNumber] = useState(1);

    const [searchParameters, setSearchParameters] = useState({
        startDate: new Date(moment().subtract(10, 'days').format('YYYY-MM-DD')),
        endDate: new Date()
    });

    const [statusList, setStatusList] = useState([]);

    useEffect(() => {
        if (parameter && value) {
            getDailyStatus();
        }
    }, [parameter, value, searchParameters.startDate, searchParameters.endDate]);

    const getDailyStatus = async () => {
        try {
            const paramName = decrypt(parameter);
            const paramValue = decrypt(value);
            if (paramName === "projectID") {
                setIsProject(true);
            }
            const start = searchParameters.startDate && moment(searchParameters.startDate).isValid()
                ? moment(searchParameters.startDate).format('YYYY-MM-DD')
                : '';
            const end = searchParameters.endDate && moment(searchParameters.endDate).isValid()
                ? moment(searchParameters.endDate).format('YYYY-MM-DD')
                : '';
            const resp = await fetchData({ url: `${URLS.getDailyStatus}?startDate=${start}&endDate=${end}&${paramName}=${paramValue}`, method: 'get' });
            if (resp.status === 200) {
                setStatusList(resp.data);
                setNodata(true);
            }
        } catch (error) {
            console.log(error);
        }
    };
    const getDailyStatusdownload = async () => {
        try {
            const paramName = decrypt(parameter);
            const paramValue = decrypt(value);          
            const start = searchParameters.startDate && moment(searchParameters.startDate).isValid() ? moment(searchParameters.startDate).format('YYYY-MM-DD') : '';
            const end = searchParameters.endDate && moment(searchParameters.endDate).isValid() ? moment(searchParameters.endDate).format('YYYY-MM-DD') : '';
            const resp = await fetchData({ url: `${URLS.getDailyStatusdownload}?startDate=${start}&endDate=${end}&${paramName}=${paramValue}`, method: 'get' });
            if (resp.status === 200) {
                window.open(`${URLS.fileDownloadServer}${resp.data.location}/${resp.data.fileName}`)
                setNodata(true)
            } 
            else 
            {
                toast.error(resp.message || 'Error downloading file')
            }
        } catch (error) {
            console.log(error);
        }
    }


    

    const getPageNo = (data) => {
        setPageNumber(data)
    }

    /* whose reports these are */
    const firstEntry = statusList?.[0]?.data?.[0];
    const subjectName = state?.title
        || (isProject ? firstEntry?.project?.projectName : firstEntry?.employee?.employeeName)
        || "";

    const pageTitle = isProject
        ? `Project Report${subjectName ? ` · ${subjectName}` : ""}`
        : `Work Report${subjectName ? ` · ${subjectName}` : ""}`;

    /* Everyone seen across the whole range, used as the denominator when the
       opening page did not pass a team size. */
    const seenPeople = new Set();
    statusList.forEach((row) => (row.data || []).forEach((entry) => {
        if (entry?.employee?.employeeCode) seenPeople.add(entry.employee.employeeCode);
    }));

    const teamTotal = state?.total || seenPeople.size;

    const submittedOn = (row) => new Set(
        (row.data || []).map((entry) => entry?.employee?.employeeCode).filter(Boolean)).size;

    const [rowsPerPage, setRowsPerPage] = useState(15);

    return (
        <div className='content wh-table'>
            <div className="wh-toolbar">
                <h4 className='title'>{pageTitle}</h4>
            </div>

            {/* One filter bar that wraps, in place of the second copy this page
                carried for small screens — which had no Download button. */}
            <div className="wh-filter-bar">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        label="Start Date"
                        inputFormat="DD/MM/YYYY"
                        value={searchParameters.startDate}
                        maxDate={new Date()}
                        onChange={(date) => {
                            const validDate = date && date.toDate ? date.toDate() : date;
                            setSearchParameters(prev => ({ ...prev, startDate: validDate }));
                        }}
                        renderInput={(params) => <TextField {...params} size="small" required />}
                    />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        label="End Date"
                        inputFormat="DD/MM/YYYY"
                        value={searchParameters.endDate}
                        minDate={searchParameters.startDate}
                        maxDate={new Date()}
                        onChange={(date) => {
                            const validDate = date && date.toDate ? date.toDate() : date;
                            setSearchParameters(prev => ({ ...prev, endDate: validDate }));
                        }}
                        renderInput={(params) => <TextField {...params} size="small" required />}
                    />
                </LocalizationProvider>

                <Button variant="contained" startIcon={<SearchRoundedIcon />}
                    onClick={(e) => { getDailyStatus(); }}>Search</Button>

                <Button variant="outlined" startIcon={<DownloadRoundedIcon />}
                    className="wh-filter-bar-end"
                    disabled={statusList.length ? false : true}
                    onClick={(e) => { getDailyStatusdownload(); }}>Download</Button>
            </div>

            {statusList.length > 0 ?
                <>
                    <Accordion defaultActiveKey="0" className="wh-accordion">
                        {statusList.slice((pageNumber - 1) * rowsPerPage,
                            pageNumber * rowsPerPage).map((row, index) => {

                                const hours = row.data.reduce((sum, item) => sum + Number(item.hours || 0), 0);

                                return (
                                    <Accordion.Item key={index} eventKey={index}>
                                        <Accordion.Header>
                                            <span className="wh-day-date">
                                                <CalendarMonthOutlinedIcon />
                                                {moment(row.date).format('DD MMMM YYYY')}
                                            </span>
                                            <span className="wh-day-meta">
                                                {isProject && teamTotal > 0 && (
                                                    <span className={`wh-pill ${submittedOn(row) >= teamTotal ? 'wh-pill-good' : 'wh-pill-warn'}`}>
                                                        {submittedOn(row)} of {teamTotal} submitted
                                                    </span>
                                                )}
                                                <span className="wh-pill">{row.data.length} {row.data.length === 1 ? 'entry' : 'entries'}</span>
                                                <span className="wh-pill wh-pill-good">{hours} {hours === 1 ? 'hour' : 'hours'}</span>
                                            </span>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className="table-responsive wh-table-shell">
                                                <table className="table table-bordered">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">#</th>
                                                            {isProject && <th scope="col">Employee Name</th>}
                                                            {isProject && <th scope="col">Employee Code</th>}
                                                            <th scope="col">Project Name</th>
                                                            <th scope="col">Activity Name</th>
                                                            <th scope="col">Hours</th>
                                                            <th scope="col">Description</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {row.data.map((s, subIndex) =>
                                                            <tr key={subIndex}>
                                                                <th scope="row">{subIndex + 1}</th>
                                                                {isProject && <td>{s.employee.employeeName}</td>}
                                                                {isProject && <td>{s.employee.employeeCode}</td>}
                                                                <td>{s.project.projectName}</td>
                                                                <td>{s.Activity.activityName}</td>
                                                                <td className="text-nowrap">{s.hours}</td>
                                                                <td className="wh-cell-muted">
                                                                    <Description text={s.description} />
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                );
                            })}
                    </Accordion>

                    {/* outside the Accordion — it is not one of its items */}
                    <PaginationGlobal
                        count={Math.ceil(statusList.length / rowsPerPage)}
                        pageNumber={pageNumber} func={getPageNo}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(size) => { setRowsPerPage(size); setPageNumber(1); }} />
                </>
                : (isNodata ? <NoData /> : <SkeletonLoader />)
            }
        </div>
    );
}

export default ViewStatus;